import React, { Component } from 'react';
import dayjs from 'dayjs';
import Select from 'react-select';

import reportServices from '@C/Reports/_utils/data';
import utilsServices from '@C/Utils/data';
import { formatCurrency, TableGrid } from '@C/TableGrid';
import { DateFilter } from '@C/Utils';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authService } from '@S';

const cashTypeOptions = [
  { value: '', label: 'All' },
  { value: '1003', label: 'Total Cash in hand' },
  { value: '1000', label: 'Cash received from office' },
  { value: '1002', label: 'Cash return to office' },
  { value: '1001', label: 'Cash given to manager' },
];

const denominationColumns = [500, 200, 100, 50, 20, 10, 5, 2, 1];

const col = [
  {
    name: 'Staff',
    key: 'all',
    formatter(row) {
      const content = row.staff_name || '';
      return row.isTotalRow ? <strong>{content}</strong> : <>{content}</>;
    },
  },
  {
    name: 'Shift',
    key: 'all',
    formatter(row) {
      const content = row.shift_name || '';
      return row.isTotalRow ? <strong>{content}</strong> : <>{content}</>;
    },
  },
  {
    name: 'Total amount',
    key: 'all',
    formatter(row) {
      const content = formatCurrency(row.amount || 0, 0);
      return row.isTotalRow ? <strong>{content}</strong> : <>{content}</>;
    },
  },
  ...denominationColumns.map((denomination) => ({
    name: `${denomination}`,
    key: 'all',
    formatter(row) {
      const content = Number(row[`denomination_${denomination}`] || 0);
      return row.isTotalRow ? <strong>{content}</strong> : <>{content}</>;
    },
  })),
];

class DenominationReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      denominationList: [],
      staffOptions: [{ value: '', label: 'All' }],
      shiftOptions: [{ value: '', label: 'All' }],
      cashier_duty_id: '',
      shift_id: '',
      date: dayjs().format('YYYY-MM-DD'),
      loading: false,
      limit: 20,
      offset: 1,
      count: 0,
      cashType: '',
    };
    this.limitInput = React.createRef();
  }

  componentDidMount() {
    this.getFilterOptions();
    //this.getDenominationReport();
  }

  getFilterOptions = async () => {
    try {
      const [staffResponse, shiftResponse] = await Promise.all([
        utilsServices.getStaffList(),
        utilsServices.getShiftsList(),
      ]);

      const rawStaff = staffResponse?.data?.data || [];
      const normalizedStaff =
        authService.currentUserRole === 'Dealer'
          ? [...(rawStaff.cashiers || []), ...(rawStaff.managers || [])]
          : [...rawStaff];

      const staffOptions = [
        { value: '', label: 'All' },
        ...normalizedStaff.map((staff) => ({
          value: staff.uuid,
          label: `${staff.first_name} ${staff.last_name} (${staff.role})`,
        })),
      ];

      const shiftOptions = [
        { value: '', label: 'All' },
        ...(shiftResponse?.data?.data || []).map((shift) => ({
          value: shift.uuid,
          label: shift.shift,
        })),
      ];

      this.setState({ staffOptions, shiftOptions });
    } catch (error) {
      console.log(error);
    }
  };

  getDenominationReport = () => {
    this.setState({ loading: true });
    reportServices
      .getDenominationReport({
        ...this.state,
        sortOrder: this.state.cashType,
      })
      .then((response) => {
        const results = response?.data?.data?.results || [];
        this.setState({
          denominationList: results,
          count: response?.data?.data?.count || 0,
          loading: false,
        });
      })
      .catch((error) => {
        console.log(error);
        this.setState({ loading: false });
      });
  };

  handleStaffChange = (option) => {
    this.setState({ cashier_duty_id: option?.value || '' });
  };

  handleShiftChange = (option) => {
    this.setState({ shift_id: option?.value || '' });
  };

  handleCashTypeChange = (option) => {
    this.setState({ cashType: option?.value || '' });
  };

  handleDateChange = (e) => {
    this.setState({ date: e.target.value });
  };

  handleQuickSearch = (e) => {
    e.preventDefault();
    if (
      this.limitInput.current.value === '' ||
      this.limitInput.current.value === '0'
    ) {
      return;
    }

    this.setState(
      { limit: Number(this.limitInput.current.value), offset: 1 },
      () => {
        this.getDenominationReport();
      },
    );
  };

  resetFilter = () => {
    if (this.limitInput.current) {
      this.limitInput.current.value = 20;
    }

    this.setState(
      {
        cashier_duty_id: '',
        shift_id: '',
        cashType: '',
        date: dayjs().format('YYYY-MM-DD'),
        limit: 20,
        offset: 1,
      },
      () => {
        this.getDenominationReport();
      },
    );
  };

  submitForm = (e) => {
    e.preventDefault();
    this.setState({ offset: 1 }, () => {
      this.getDenominationReport();
    });
  };

  getDenominationCount = (row, denomination) => {
    return (
      (row?.denomination_list || []).find(
        (item) => item.denomination_details__value === denomination,
      )?.count || 0
    );
  };

  getTotals = (rows = []) => {
    return rows.reduce(
      (acc, row) => {
        acc.amount += Number(row.amount || 0);
        denominationColumns.forEach((denomination) => {
          acc.counts[denomination] += Number(
            row[`denomination_${denomination}`] || 0,
          );
        });
        return acc;
      },
      {
        amount: 0,
        counts: denominationColumns.reduce((obj, denomination) => {
          obj[denomination] = 0;
          return obj;
        }, {}),
      },
    );
  };

  getTableRows = () => {
    const rows = this.state.denominationList.map((item, index) => {
      const row = {
        id: `denomination-row-${index}`,
        staff_name:
          `${item.cashier_first_name || ''} ${item.cashier_last_name || ''}`.trim(),
        shift_name: item.shift || '--',
        amount: Number(item.amount || 0),
      };

      denominationColumns.forEach((denomination) => {
        row[`denomination_${denomination}`] = this.getDenominationCount(
          item,
          denomination,
        );
      });

      return row;
    });

    if (rows.length === 0) {
      return rows;
    }

    const totals = this.getTotals(rows);
    const totalRow = {
      id: 'denomination-total-row',
      staff_name: '',
      shift_name: 'TOTAL',
      amount: totals.amount,
      isTotalRow: true,
    };

    denominationColumns.forEach((denomination) => {
      totalRow[`denomination_${denomination}`] = totals.counts[denomination];
    });

    return [...rows, totalRow];
  };

  render() {
    const {
      denominationList,
      staffOptions,
      shiftOptions,
      cashier_duty_id,
      shift_id,
      cashType,
      loading,
      count,
      limit,
      date,
    } = this.state;
    const tableRows = this.getTableRows();
    const totals = this.getTotals(tableRows.filter((row) => !row.isTotalRow));

    return (
      <section className="widget list-table full-width inverted">
        <header className="list-header">
          <h3>Denomination</h3>
        </header>
        <main>
          <div className="table-filters">
            <div className="filter-wrapper">
              <div className="filter-group">
                <label>Date</label>
                <DateFilter
                  className="cstm-date-input"
                  value={date}
                  name="date"
                  onChange={this.handleDateChange}
                />
              </div>
              <div className="filter-group">
                <label>Staff</label>
                <Select
                  options={staffOptions}
                  onChange={this.handleStaffChange}
                  value={staffOptions.find(
                    (option) => option.value === cashier_duty_id,
                  )}
                  className="staff-selector-container"
                  classNamePrefix="staff-selector"
                  placeholder="All"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="filter-group">
                <label>Shift</label>
                <Select
                  options={shiftOptions}
                  onChange={this.handleShiftChange}
                  value={shiftOptions.find(
                    (option) => option.value === shift_id,
                  )}
                  className="shift-selector-container"
                  classNamePrefix="shift-selector"
                  placeholder="All"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="filter-group">
                <label>Cash type</label>
                <Select
                  options={cashTypeOptions}
                  onChange={this.handleCashTypeChange}
                  value={cashTypeOptions.find(
                    (option) => option.value === cashType,
                  )}
                  className="select-box-container"
                  classNamePrefix="raw-select"
                  placeholder="All"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="filter-group with-icon">
                <label>
                  Results to display- {limit || 20} (Total: {count})
                </label>
                <form onSubmit={this.handleQuickSearch}>
                  <input
                    type="text"
                    name="limit"
                    ref={this.limitInput}
                    defaultValue={20}
                  />
                  <button
                    type="submit"
                    className="search-icon"
                    onClick={this.handleQuickSearch}
                  />
                </form>
              </div>
              <div className="filter-group reset-filter">
                <button onClick={this.resetFilter}>Reset filters</button>
              </div>
              <div className="filter-group">
                <button
                  className={`submit ${loading ? 'loading' : ''}`}
                  disabled={loading}
                  type="submit"
                  onClick={this.submitForm}
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>

          <div className="table-wrapper">
            <div className="mini-summary">
              <ul>
                <li>
                  <span>Total amount</span>
                  <span>{formatCurrency(totals.amount || 0)}</span>
                </li>
              </ul>
            </div>

            {loading ? (
              <DropLoaderLarge />
            ) : (
              <TableGrid
                col={col}
                row={tableRows}
                offset={this.state.offset}
                count={this.state.count}
                limit={this.state.limit}
                handlePagination={() => false}
                hideActions={true}
                pagination="disabled"
                exportOptions={{
                  fileName: `Denomination - ${this.state.date}.pdf`,
                  reportName: 'Denomination Report',
                  reportDate: `Date - ${dayjs(this.state.date).format('D MMM YY')}`,
                  reportTotal: `Total amount: ${formatCurrency(
                    totals.amount || 0,
                    0,
                  )}`,
                  reportQuantity: '',
                  reportSales: '',
                  reportSaleTotal: '',
                  reportTCSTotal: '',
                  reportTesting: '',
                  reportBowser: '',
                  reportCustomerName: '',
                  reportGst: '',
                  reportOpeningBalance: '',
                  reportClosingBalance: '',
                }}
              />
            )}
          </div>
        </main>
      </section>
    );
  }
}

export { DenominationReport };
